import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import config from '../config/config';
import PaymentModal from "../components/PaymentModal";
import EditInvoiceModal from "../components/EditInvoiceModal";
import MUIDataTable from 'mui-datatables';
import { useNavigate } from 'react-router-dom';
import DynamicNav from "../config/dynamicNav";
import {
  Typography, Box, Paper, Grid, Chip, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Skeleton, IconButton,
  Alert, Snackbar, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Menu, MenuItem, Select, FormControl,
  InputLabel, ToggleButton, ToggleButtonGroup, CircularProgress, useTheme,
} from '@mui/material';
import {
  Check, Warning, ErrorOutline, InsertDriveFile, FileDownload,
  Payment, Edit, LocalOffer, Close,
} from '@mui/icons-material';
import '../index.css';
import TopBar from "../components/TopBar";
import SideBar from "../components/SideBar";

/* ─── Global style injection ─────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap');
    body, * { font-family: 'Quicksand', sans-serif !important; }
  `}</style>
);

/* ─── Design tokens ───────────────────────────────────────────────────────── */
const T = {
  white: '#ffffff',
  bg: '#f8f9fc',
  surface: '#ffffff',
  border: '#e8ecf4',
  borderLight: '#f0f3fb',
  accent: '#5b4fcf',
  accentLight: '#ede9fd',
  accentMid: '#7c6fe0',
  blue: '#3b82f6',
  text: '#1a1d2e',
  textSub: '#6b7280',
  textMuted: '#9ca3af',
  success: '#059669',
  successBg: '#ecfdf5',
  successBorder: '#a7f3d0',
  warning: '#d97706',
  warningBg: '#fffbeb',
  error: '#dc2626',
  errorBg: '#fef2f2',
  shadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 24px rgba(0,0,0,0.08)',
  radius: '14px',
  radiusSm: '8px',
};

/* ─── Shared sx helpers ───────────────────────────────────────────────────── */
const card = {
  background: T.surface,
  borderRadius: T.radius,
  border: `1px solid ${T.border}`,
  boxShadow: T.shadow,
};

const pillBtn = (variant = 'accent') => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: 'Quicksand, sans-serif',
  fontSize: '13px',
  px: 2.5,
  py: 1,
  transition: 'all 0.18s ease',
  ...(variant === 'accent' && {
    background: T.accent,
    color: '#fff',
    border: 'none',
    '&:hover': { background: T.accentMid, boxShadow: '0 4px 14px rgba(91,79,207,0.35)' },
  }),
  ...(variant === 'outline' && {
    background: T.white,
    color: T.accent,
    border: `1.5px solid ${T.accent}`,
    '&:hover': { background: T.accentLight },
  }),
  ...(variant === 'ghost' && {
    background: T.bg,
    color: T.textSub,
    border: `1px solid ${T.border}`,
    '&:hover': { background: T.border },
  }),
});

/* ─── Status chip ─────────────────────────────────────────────────────────── */
const InvoiceStatus = ({ status }) => {
  const map = {
    paid:    { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0', icon: <Check sx={{ fontSize: 14 }} />, label: 'Paid' },
    pending: { bg: '#fffbeb', color: '#d97706', border: '#fcd34d', icon: <Warning sx={{ fontSize: 14 }} />, label: 'Pending' },
    overdue: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', icon: <ErrorOutline sx={{ fontSize: 14 }} />, label: 'Overdue' },
    draft:   { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1', icon: <InsertDriveFile sx={{ fontSize: 14 }} />, label: 'Draft' },
  };
  const c = map[(status || 'pending').toLowerCase()] || map.pending;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: '8px', px: 1.2, py: 0.4, fontSize: '12px', fontWeight: 700,
    }}>
      {c.icon}{c.label}
    </Box>
  );
};

/* ─── Formatters ──────────────────────────────────────────────────────────── */
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/* ─── Stat card ───────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, accent }) => (
  <Box sx={{
    background: accent ? T.accentLight : T.bg,
    borderRadius: '12px',
    border: `1px solid ${accent ? '#d4cff7' : T.borderLight}`,
    px: 2, py: 1.5, flex: 1,
  }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: accent ? T.accent : T.text }}>
      {value}
    </Typography>
  </Box>
);

/* ─── Discount Dialog ─────────────────────────────────────────────────────── */
const DiscountDialog = ({ open, onClose, invoice, invoiceItems, onSuccess }) => {
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const tuitionItem = invoiceItems.find(i =>
    i.description?.toLowerCase().includes('tuition') || i.description?.toLowerCase().includes('fee')
  );
  const tuitionAmount = tuitionItem ? parseFloat(tuitionItem.total || 0) : 0;

  const computedDiscount = useMemo(() => {
    const v = parseFloat(value) || 0;
    if (mode === 'percent') return tuitionAmount > 0 ? (v / 100) * tuitionAmount : 0;
    return v;
  }, [value, mode, tuitionAmount]);

  const handleApply = async () => {
    setError('');
    setSuccess('');
    const v = parseFloat(value);
    if (!v || v <= 0) { setError('Please enter a valid discount value.'); return; }
    if (mode === 'percent' && v > 100) { setError('Percentage cannot exceed 100%.'); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
    
await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = {
        invoice_id: invoice?.id,
        discount_type: mode,
        discount_value: v,
        discount_amount: computedDiscount,
      };

      await axios.post(`${config.API_URL}/api/set-discount`, payload, {
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
      });

      setSuccess(`Discount of ${formatCurrency(computedDiscount)} applied successfully.`);
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to apply discount. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValue('');
    setError('');
    setSuccess('');
    setMode('percent');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
      sx: { borderRadius: '18px', border: `1px solid ${T.border}`, boxShadow: T.shadowMd, fontFamily: 'Quicksand, sans-serif' }
    }}>
      <DialogTitle sx={{ pb: 1.5, borderBottom: `1px solid ${T.border}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocalOffer sx={{ fontSize: 18, color: T.accent }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: T.text, fontFamily: 'Quicksand, sans-serif' }}>
                Apply Discount
              </Typography>
              <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                Invoice #{invoice?.invoiceNumber}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: 'Quicksand, sans-serif' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontFamily: 'Quicksand, sans-serif' }}>{success}</Alert>}

        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: T.textSub, mb: 1.5, fontFamily: 'Quicksand, sans-serif' }}>
          Discount Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
          {[{ key: 'percent', label: '% of Tuition Fee' }, { key: 'fixed', label: 'Fixed Amount (KES)' }].map(opt => (
            <Box key={opt.key} onClick={() => { setMode(opt.key); setValue(''); }}
              sx={{
                flex: 1, py: 1.2, borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                border: `2px solid ${mode === opt.key ? T.accent : T.border}`,
                background: mode === opt.key ? T.accentLight : T.white,
                color: mode === opt.key ? T.accent : T.textSub,
                fontWeight: 700, fontSize: '13px', fontFamily: 'Quicksand, sans-serif',
                transition: 'all 0.15s ease',
              }}>{opt.label}</Box>
          ))}
        </Box>

        {mode === 'percent' && tuitionAmount > 0 && (
          <Box sx={{ background: T.bg, borderRadius: '10px', px: 2, py: 1.5, mb: 2.5, border: `1px solid ${T.borderLight}` }}>
            <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
              Tuition Fee base: <strong style={{ color: T.text }}>{formatCurrency(tuitionAmount)}</strong>
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          label={mode === 'percent' ? 'Discount Percentage (%)' : 'Discount Amount (KES)'}
          type="number"
          value={value}
          onChange={e => setValue(e.target.value)}
          inputProps={{ min: 0, max: mode === 'percent' ? 100 : undefined, step: mode === 'percent' ? '0.01' : '1' }}
          disabled={loading}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 },
            '& .MuiInputLabel-root': { fontFamily: 'Quicksand, sans-serif', fontWeight: 600 },
          }}
        />

        {parseFloat(value) > 0 && (
          <Box sx={{
            background: T.accentLight, borderRadius: '12px', px: 2.5, py: 2,
            border: `1px dashed #c4bcf7`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: T.accent, fontFamily: 'Quicksand, sans-serif' }}>
                Discount Preview
              </Typography>
              <Typography sx={{ fontSize: '11px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                {mode === 'percent' ? `${value}% of ${formatCurrency(tuitionAmount)}` : 'Fixed amount'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: T.accent, fontFamily: 'Quicksand, sans-serif' }}>
              {formatCurrency(computedDiscount)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={pillBtn('ghost')}>Cancel</Button>
        <Button onClick={handleApply} disabled={loading} variant="contained" sx={pillBtn('accent')} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <LocalOffer sx={{ fontSize: 16 }} />}>
          {loading ? 'Applying...' : 'Apply Discount'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─── Main component ──────────────────────────────────────────────────────── */
const StudentInvoicesLayout = () => {
  const { id, invoice_id } = useParams();
  const theme = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [studentInfo, setStudentInfo] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [latestInvoice, setInvoiceLatest] = useState(null);
  const [previousInvoices, setPreviousInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [invoicesData, setInvoicesData] = useState([]);
  const [getInvoiceItems, setInvoiceItems] = useState([]);
  const [invoiceInfo, setInvoiceInfo] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInputs, setPaymentInputs] = useState({});
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditOpen, setEditModalOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const invoiceRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const handleMarkPaid = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);
  const handleEditModalClose = () => setEditModalOpen(false);

  const handleMenuClick = (event, student) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedStudent(student);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const handleView = () => { setOpenModal(true); handleMenuClose(); };
  const handleCloseModal = () => setOpenModal(false);

  const getToken = () => localStorage.getItem('token');
  const token_ = getToken();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || config.API_URL + '/api';

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  };

  const handleSubmitPayment = async (payload) => {
    try {
      const token = localStorage.getItem("token");
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');
      const response = await axios.post(`${config.API_URL}/api/savePayments`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json', 'X-XSRF-TOKEN': xsrfToken },
        withCredentials: true,
      });
      const data = response.data;
      window.location.assign(`/students/${payload.invoice_id}/viewinvoice`);
      setNotification({ open: true, message: data.message || "Payment saved successfully.", severity: 'success' });
      return { success: true, message: data.message || "Payment saved successfully." };
    } catch (error) {
      setNotification({ open: true, message: error.message || "Network error.", severity: 'error' });
      return { success: false, message: error.message || "Network error." };
    }
  };

  const handleEditSubmitPayment = async (payload) => {
    try {
      const token = localStorage.getItem("token");
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');
      const response = await fetch(`${config.API_URL}/api/saveInvoiceChanges`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json', 'X-XSRF-TOKEN': xsrfToken },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (!response.ok) {
        let message = "Failed to save payment.";
        try { const json = JSON.parse(text); message = json.message || message; } catch (_) { if (text) message = text; }
        return { success: false, message };
      }
      const data = text ? JSON.parse(text) : {};
      window.location.assign(`/students/${payload.invoice_id}/viewinvoice`);
      return { success: true, message: data.message || "Payment saved successfully." };
    } catch (error) {
      return { success: false, message: error.message || "Network error." };
    }
  };

  const handleEditModal = async (item_id) => {
    setEditModalOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(config.API_URL + `/api/pullInvoiceItem/${item_id}`, { headers: { Authorization: `Bearer ${token_}` } });
      setInvoiceItems(res.data.invoice_items);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });
  api.interceptors.request.use((cfg) => { const t = getToken(); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; }, (error) => Promise.reject(error));

  useEffect(() => {
    axios.get(config.API_URL + `/api/studentInvoice/${id}`, { headers: { Authorization: `Bearer ${token_}` } })
      .then(r => setInvoiceInfo(r.data)).catch(console.error);

    axios.get(config.API_URL + `/api/latestInvoiceV2/${id}`, { headers: { Authorization: `Bearer ${token_}` } })
      .then(r => {
        console.log(r);
        setInvoiceLatest({ id: r.data.id, customer_id: r.data.customer_id, invoiceNumber: r.data.invoiceNumber, invoiceIssueDate: r.data.invoiceIssueDate, invoiceDueDate: r.data.invoiceDueDate, invoiceAmount: r.data.invoiceAmount, status: r.data.status, raw: r.data.invoice,total_discount:r.data.total_discount });
        setInvoiceItems(r.data.invoice_items);
        setPreviousInvoices(r.data.otherInvoices);
        setAccounts(r.data.accounts);
       
      }).catch(console.error);
  }, [id]);

  const fetchStudents = async (pg = 0, rpp = 100, search = '') => {
    setLoading(true);
    try {
      const res = await axios.get(config.API_URL + '/api/pullpayments/' + id, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pg + 1, per_page: rpp, search },
      });
      setData(res.data.data);
       setDiscounts(res.data.discounts);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentInfo = useCallback(async () => {
    try { const res = await api.get(`/studentInvoiceInfo/${id}`); setStudentInfo(res.data); } catch (err) { console.error(err); }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true); setError(false);
    try { const res = await api.get(`/student/${id}/invoices`); setInvoicesData(res.data.data); } catch (err) { setError(true); } finally { setLoading(false); }
  }, []);

  const handleViewInvoice = (id, invoiceId) => {
    if (loading) return;
    setLoading(true);
    axios.get(config.API_URL + `/api/latestInvoice/${id}/${invoiceId}`, { headers: { Authorization: `Bearer ${token_}` } })
      .then(r => {
        setInvoiceLatest({ invoiceNumber: r.data.invoiceNumber, invoiceIssueDate: r.data.invoiceIssueDate, invoiceDueDate: r.data.invoiceDueDate, invoiceAmount: r.data.invoiceAmount, status: r.data.status, raw: r.data.invoice });
        setInvoiceItems(r.data.invoice_items);
        setPreviousInvoices(r.data.otherInvoices);
      }).catch(console.error).finally(() => setLoading(false));
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      setNotification({ open: true, message: 'Invoice download started', severity: 'info' });
      window.open(`${config.API_URL}/downloadInvoice/${id}/${token}`);
      setNotification({ open: true, message: 'Invoice downloaded successfully', severity: 'success' });
    } catch (err) {
      setNotification({ open: true, message: 'Failed to download invoice', severity: 'error' });
    }
  };

  useEffect(() => { fetchStudentInfo(); fetchInvoices(); fetchStudents(); }, []);

  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  const columns = useMemo(() => [
    { name: 'id', label: 'ID' },
    { name: 'payment_reference', label: 'Reference No' },
    { name: 'admission_number', label: 'Admission No' },
    { name: 'name', label: 'Name' },
    { name: 'amount', label: 'Amount' },
    { name: 'created_at', label: 'Paid On' },
  ], [data]);

  const options = useMemo(() => ({
    search: false, filter: true, download: true, print: true, pagination: true,
    serverSide: true, count: total, rowsPerPage, page,
    onTableChange: (action, tableState) => {
      if (action === 'changePage') setPage(tableState.page);
      if (action === 'changeRowsPerPage') { setRowsPerPage(tableState.rowsPerPage); setPage(0); }
    },
    onRowClick: (rowData, rowMeta) => {
      const student = data[rowMeta.dataIndex];
      window.open(`${config.API_URL}/downloadReceipt/${student.id}/${token}`);
    },
  }), [total, page, rowsPerPage, data, navigate]);

  const invoiceTotals = useMemo(() => ({
   // total_discount:latestInvoice?.total_discount,
    total: getInvoiceItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
    paid: getInvoiceItems.reduce((s, i) => s + (parseFloat(i.paid) || 0), 0),
    balance: getInvoiceItems.reduce((s, i) => s + (parseFloat(i.balance) || 0), 0),
    excess: getInvoiceItems.reduce((s, i) => s + (parseFloat(i.excess_amount) || 0), 0),
  }), [getInvoiceItems]);

  if (loading) {
    return (
      <div className="d-flex flex-column" id="kt_app_root">
        <GlobalStyle />
        <TopBar />
        <div className="d-flex flex-grow-1">
          <SideBar />
          <Box sx={{ flex: 1, p: 4, background: T.bg }}>
            <Skeleton variant="rectangular" height={56} width="40%" sx={{ mb: 3, borderRadius: T.radius }} />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[1, 2, 3, 4].map(i => <Grid item xs={3} key={i}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px' }} /></Grid>)}
            </Grid>
            <Skeleton variant="rectangular" height={360} sx={{ borderRadius: T.radius }} />
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column" id="kt_app_root">
        <GlobalStyle />
        <TopBar />
        <div className="d-flex flex-grow-1">
          <SideBar />
          <Box sx={{ flex: 1, p: 4, background: T.bg }}>
            <Alert severity="error" sx={{ borderRadius: T.radius }}>
              Error loading invoices. Please try again later or contact support.
            </Alert>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" id="kt_app_root">
      <GlobalStyle />
      <TopBar />
      <div className="d-flex flex-grow-1">
        <SideBar />

        <Box sx={{ flex: 1, background: T.bg, minHeight: '100vh', p: { xs: 2, md: 3.5 } }}>
          <DynamicNav />

          {/* ── Page header ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
                Johari School
              </Typography>
              <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500, mt: 0.3 }}>
                {studentInfo ? `${studentInfo.name} · Adm: ${studentInfo.admission}` : 'Student Invoice'}
              </Typography>
            </Box>

            {/* ── Action buttons ── */}
            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
              <Tooltip title="Download Invoice">
                <Button
                  startIcon={<FileDownload sx={{ fontSize: 16 }} />}
                  onClick={() => handleDownloadInvoice(latestInvoice?.id)}
                  sx={pillBtn('ghost')}
                >
                  Download
                </Button>
              </Tooltip>
              <Button
                startIcon={<LocalOffer sx={{ fontSize: 16 }} />}
                onClick={() => setDiscountOpen(true)}
                sx={pillBtn('outline')}
              >
                Discount
              </Button>
              <Button
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={() => handleEditModal(latestInvoice?.id)}
                sx={pillBtn('ghost')}
              >
                Edit
              </Button>
              <Button
                startIcon={<Payment sx={{ fontSize: 16 }} />}
                disabled={latestInvoice?.status?.toLowerCase() === 'paid'}
                onClick={handleMarkPaid}
                sx={pillBtn('accent')}
                className="text-white"
              >
                Add Payment
              </Button>
            </Box>
          </Box>

          {/* ── Summary stat cards ── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <StatCard label="Invoice Total" value={formatCurrency(invoiceTotals.total)} />
             <StatCard label="Discount" value={formatCurrency(latestInvoice?.total_discount)} />
            <StatCard label="Total Paid" value={formatCurrency(invoiceTotals.paid)} />
            <StatCard label="Balance Due" value={formatCurrency(invoiceTotals.balance)} accent />
            <StatCard label="Overpayment" value={formatCurrency(invoiceTotals.excess)} />
          </Box>

          {/* ── Invoice card ── */}
          {latestInvoice && (
            <Box ref={invoiceRef}>
              <Paper elevation={0} sx={{ ...card, p: 0, overflow: 'hidden', mb: 3 }}>

                {/* Card top bar */}
                <Box sx={{
                  px: 3.5, py: 2.5,
                  borderBottom: `1px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '18px' }}>🧾</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.text }}>
                        Invoice #{latestInvoice?.invoiceNumber}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: T.textSub }}>
                        Issued {formatDate(latestInvoice?.invoiceIssueDate)}
                      </Typography>
                    </Box>
                  </Box>
                  <InvoiceStatus status={latestInvoice?.status || 'pending'} />
                </Box>

                {/* Meta grid */}
                <Box sx={{ px: 3.5, py: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      {[
                        ['Invoice Number', latestInvoice?.invoiceNumber],
                        ['Issue Date', formatDate(latestInvoice?.invoiceIssueDate)],
                        ['Due Date', formatDate(latestInvoice?.invoiceDueDate)],
                      ].map(([label, val]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${T.borderLight}` }}>
                          <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text }}>{val}</Typography>
                        </Box>
                      ))}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {[
                        ['Invoice Amount', formatCurrency(latestInvoice?.invoiceAmount)],
                        ['Description', latestInvoice?.note || '—'],
                      ].map(([label, val]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${T.borderLight}` }}>
                          <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text }}>{val}</Typography>
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </Box>

                {/* Invoice items table */}
                <Box sx={{ px: 3.5, pb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: T.text, mb: 1.5 }}>
                    Invoice Items
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: T.bg }}>
                        {['Description', 'Quantity', 'Total', 'Paid', 'Balance', 'Overpayment'].map(h => (
                          <TableCell key={h} align={h === 'Description' ? 'left' : 'right'} sx={{
                            fontWeight: 700, fontSize: '12px', color: T.textSub,
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            py: 1.2, px: 2, borderBottom: `1px solid ${T.border}`,
                          }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getInvoiceItems.map((item, idx) => (
                        <TableRow key={idx} sx={{
                          '&:hover': { background: T.accentLight },
                          background: idx % 2 === 0 ? T.white : T.bg,
                          transition: 'background 0.1s',
                        }}>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: T.text, py: 1.5, px: 2 }}>{item.description}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: T.textSub, py: 1.5, px: 2 }}>{item.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: T.text, py: 1.5, px: 2 }}>{formatCurrency(item.total)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: T.success, fontWeight: 600, py: 1.5, px: 2 }}>{formatCurrency(item.paid)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: item.balance > 0 ? T.error : T.textSub, fontWeight: 600, py: 1.5, px: 2 }}>{formatCurrency(item.balance)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: T.textSub, py: 1.5, px: 2 }}>{formatCurrency(item.excess_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    {/* Totals row */}
                    <TableBody>
                      <TableRow sx={{ background: T.bg }}>
                        <TableCell colSpan={2} sx={{ borderTop: `2px solid ${T.border}`, py: 1.5, px: 2 }} />
                        {[invoiceTotals.total, invoiceTotals.paid, invoiceTotals.balance, invoiceTotals.excess].map((val, i) => (
                          <TableCell key={i} align="right" sx={{ borderTop: `2px solid ${T.border}`, py: 1.5, px: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: T.accent }}>
                              {formatCurrency(val)}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Payments section */}
                <Box sx={{ px: 3.5, pt: 3.5, pb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: T.text, mb: 1 }}>
                    Invoice Payments
                  </Typography>
                  <Box sx={{ height: '1px', background: T.border, mb: 2 }} />
                </Box>
                <Box sx={{ px: 2, pb: 3 }}>
                  <MUIDataTable title="" data={data} columns={columns} options={options} />
                </Box>

                <Box sx={{ px: 3.5, pt: 3.5, pb: 1 }}>
 <div className="w-full">
  <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">

    {/* Header */}
    <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
      <h2 className="text-sm font-semibold text-gray-700 tracking-wide">
        Discounts
      </h2>
      <span className="text-xs text-gray-400">
        {discounts.length} records
      </span>
    </div>

    {/* Table */}
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-700 uppercase bg-gray-50">
            <th className="px-6 py-3">Type</th>
            <th className="px-6 py-3">Value</th>
            <th className="px-6 py-3">Date</th>
            <th className="px-6 py-3 text-right">Amount</th>
          </tr>
        </thead>

        <tbody className="divide-y">
          {discounts.map((i, index) => (
            <tr
              key={i.id || index}
              className="hover:bg-gray-50 transition group"
            >
              {/* Type */}
              <td className="px-6 py-4 font-medium text-gray-800">
                {i.discount_type}
              </td>

              {/* Value */}
              <td className="px-6 py-4 text-gray-700">
                {i.discount_value}
              </td>

              {/* Date */}
              <td className="px-6 py-4 text-gray-700 text-xs">
                {i.created_at}
              </td>

              {/* Amount */}
              <td className="px-6 py-4 text-right">
                <span className="inline-block px-3 py-1 rounded-full bg-green-50 text-green-600 font-semibold text-xs">
                  {i.discount_amount}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Empty State */}
    {discounts.length === 0 && (
      <div className="text-center py-10 text-gray-400 text-sm">
        No discounts found
      </div>
    )}
  </div>
</div>
</Box>
              </Paper>



            </Box>

           


          )}

          {/* ── Modals ── */}
          <PaymentModal
            open={modalOpen}
            onClose={handleModalClose}
            invoice={latestInvoice?.id}
            invoiceItems={getInvoiceItems}
            accounts={accounts}
            onSubmit={handleSubmitPayment}
          />
          <EditInvoiceModal
            open={modalEditOpen}
            onClose={handleEditModalClose}
            invoice={latestInvoice}
            invoiceItems={getInvoiceItems}
            onSubmit={handleEditSubmitPayment}
          />
          <DiscountDialog
            open={discountOpen}
            onClose={() => setDiscountOpen(false)}
            invoice={latestInvoice}
            invoiceItems={getInvoiceItems}
            onSuccess={() => { fetchInvoices(); fetchStudents(); }}
          />

          <Menu id="actions-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleView}>View</MenuItem>
          </Menu>

          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ borderRadius: '12px', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
              {notification.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default StudentInvoicesLayout;